import { Head, Link } from "@inertiajs/react"
import { ChevronDown, CirclePlus, SquarePen } from "lucide-react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import { ensureUrl } from "@/lib/format-url"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Customer {
  id: number
  company_name: string | null
  first_name: string | null
  last_name: string | null
  sort_name: string | null
  display_name: string
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  office_phone: string | null
  cell_phone: string | null
  home_phone: string | null
  fax: string | null
  email: string | null
  website: string | null
  notes: string | null
  internal_notes: string | null
  status: number
  client_type: string | null
  client_type_id: number | null
  sales_rep: string | null
  sales_rep_id: number | null
  created_at: string
  updated_at: string
  created_by_name: string | null
  updated_by_name: string | null
  locations_count: number
  contacts_count: number
  marketing_events_count: number
}

interface OpenCase {
  id: number
  case_number: string
  hpi_name: string | null
  display_name: string
  investigation_type: string | null
  manager: string | null
  intake_by: string | null
  open_date: string | null
  status_label: string
}

interface HistoryMonth {
  month: number
  qty: number
  value: number
}

interface HistoryYear {
  year: number
  months: HistoryMonth[]
}

interface ReferralRef {
  id: number
  name: string
}

interface MenuCounts {
  tasks: number
  cases_potential: number
  cases_open: number
  cases_closed: number
  case_updates: number
  logs: number
  contacts: number
  documents: number
  locations: number
  marketing_events: number
  schedules: number
  expenses_total: number
  time_count: number
}

interface AddPermissions {
  can_add_case: boolean
  can_add_customer: boolean
  can_add_schedule: boolean
}

interface Props {
  customer: Customer
  open_cases: OpenCase[]
  referral_customer: ReferralRef | null
  vendor_referral: ReferralRef | null
  marketing_categories: string[]
  case_history: HistoryYear[]
  menu_counts: MenuCounts
  add_permissions: AddPermissions
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDate(date: string | null): string {
  return (
    formatDateUtil(date, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }) || "-"
  )
}

function formatDateTime(date: string | null): string {
  return formatDateUtil(date, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

/* ── Top Bar Helpers ────────────────────────────────────── */

function TabLink({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors ${className}`}
    >
      {children}
    </a>
  )
}

function TabDropdown({
  label,
  items,
}: {
  label: string
  items: { label: string; href: string }[]
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors outline-none">
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item) => (
          <DropdownMenuItem key={item.href} asChild>
            <a href={item.href} className="cursor-pointer">
              {item.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/* ── Open Cases Columns ────────────────────────────────── */

const openCaseColumns: HpiColumn<OpenCase>[] = [
  {
    header: "Case",
    render: (row) => (
      <Link
        href={`/cases/${row.id}`}
        className="text-hpi-active-blue text-xs font-semibold hover:underline"
      >
        {row.display_name}
      </Link>
    ),
  },
  {
    header: "Status",
    width: "max-w-[100px]",
    render: (row) => <div className="text-xs">{row.status_label}</div>,
  },
  {
    header: "Intake",
    width: "max-w-[120px]",
    render: (row) => <div className="text-xs">{row.intake_by ?? "-"}</div>,
  },
  {
    header: "Mgr",
    width: "max-w-[120px]",
    render: (row) => <div className="text-xs">{row.manager ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-xs break-words">{row.investigation_type ?? "-"}</div>
    ),
  },
  {
    header: "Opened",
    width: "max-w-[100px]",
    render: (row) => <div className="text-xs">{formatDate(row.open_date)}</div>,
  },
]

/* ── Main Component ─────────────────────────────────────── */

export default function Show({
  customer,
  open_cases,
  referral_customer,
  vendor_referral,
  marketing_categories,
  case_history,
  menu_counts: mc,
  add_permissions: ap,
}: Props) {
  const c = customer
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: c.display_name, href: `/customers/${c.id}` },
  ]

  // History totals
  const totalCases = case_history.reduce(
    (sum, y) => sum + y.months.reduce((ms, m) => ms + m.qty, 0),
    0,
  )
  const totalValue = case_history.reduce(
    (sum, y) => sum + y.months.reduce((ms, m) => ms + m.value, 0),
    0,
  )
  const hasHistory = totalCases > 0

  /* -- Build Cases dropdown items -- */
  const casesItems: { label: string; href: string }[] = []
  if (mc.cases_potential > 0)
    casesItems.push({
      label: `Potential (${mc.cases_potential})`,
      href: `/cases?customer_id=${c.id}&status=potential`,
    })
  if (mc.cases_open > 0)
    casesItems.push({
      label: `Open (${mc.cases_open})`,
      href: `/cases?customer_id=${c.id}&status=open`,
    })
  if (mc.cases_closed > 0)
    casesItems.push({
      label: `Closed (${mc.cases_closed})`,
      href: `/cases?customer_id=${c.id}&status=closed`,
    })

  /* -- Build Updates dropdown items -- */
  const updatesItems: { label: string; href: string }[] = []
  if (mc.case_updates > 0)
    updatesItems.push({
      label: `Case Updates (${mc.case_updates})`,
      href: `/cases?customer_id=${c.id}&view=updates`,
    })
  if (mc.logs > 0)
    updatesItems.push({
      label: `Log Notes (${mc.logs})`,
      href: `/customers/${c.id}/logs`,
    })

  /* -- Build Activity dropdown items -- */
  const activityItems: { label: string; href: string }[] = []
  if (mc.expenses_total > 0)
    activityItems.push({
      label: `Expenses (${formatCurrency(mc.expenses_total)})`,
      href: `/customers/${c.id}/expenses`,
    })
  if (mc.time_count > 0)
    activityItems.push({
      label: `Time (${mc.time_count})`,
      href: `/customers/${c.id}/time_entries`,
    })

  /* -- Build Reports dropdown items -- */
  const reportsItems: { label: string; href: string }[] = [
    {
      label: "Referrals",
      href: `/customers/${c.id}/reports/referrals`,
    },
    {
      label: "Time Report",
      href: `/customers/${c.id}/reports/time`,
    },
  ]

  /* -- Build Add dropdown items (permission-based, sorted) -- */
  const addItems: { label: string; href: string }[] = []
  if (ap.can_add_case)
    addItems.push({
      label: "Case",
      href: `/cases/new?customer_id=${c.id}`,
    })
  if (ap.can_add_customer)
    addItems.push({
      label: "Contact",
      href: `/customers/${c.id}/contacts/new`,
    })
  addItems.push({
    label: "Document",
    href: `/customers/${c.id}/documents/new`,
  })
  addItems.push({
    label: "Expense",
    href: `/customers/${c.id}/expenses/new`,
  })
  if (ap.can_add_customer)
    addItems.push({
      label: "Location",
      href: `/customers/${c.id}/locations/new`,
    })
  addItems.push({
    label: "Log Note",
    href: `/customers/${c.id}/logs/new?type=log_note`,
  })
  if (ap.can_add_schedule)
    addItems.push({
      label: "Schedule",
      href: `/customers/${c.id}/schedules/new`,
    })
  addItems.push({
    label: "Task",
    href: `/customers/${c.id}/logs/new?type=todo`,
  })
  addItems.push({
    label: "Time",
    href: `/customers/${c.id}/time_entries/new`,
  })
  addItems.sort((a, b) => a.label.localeCompare(b.label))

  const customerTabsBar = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6">
        {/* Tasks - visible when count > 0, styled red */}
        {mc.tasks > 0 && (
          <a
            href={`/cases?customer_id=${c.id}&view=tasks`}
            className="flex items-center gap-1 text-sm text-red-600 transition-colors hover:text-red-800"
          >
            Tasks ({mc.tasks})
          </a>
        )}

        {/* Cases dropdown */}
        {casesItems.length > 0 && (
          <TabDropdown label="Cases" items={casesItems} />
        )}

        {/* Updates dropdown */}
        {updatesItems.length > 0 && (
          <TabDropdown label="Updates" items={updatesItems} />
        )}

        {/* Activity dropdown */}
        {activityItems.length > 0 && (
          <TabDropdown label="Activity" items={activityItems} />
        )}

        {/* Contacts link */}
        {mc.contacts > 0 && (
          <TabLink href={`/customers/${c.id}/contacts`}>
            Contacts ({mc.contacts})
          </TabLink>
        )}

        {/* Documents link */}
        {mc.documents > 0 && (
          <TabLink href={`/customers/${c.id}/documents`}>
            Documents ({mc.documents})
          </TabLink>
        )}

        {/* Locations link */}
        {mc.locations > 0 && (
          <TabLink href={`/customers/${c.id}/locations`}>
            Locations ({mc.locations})
          </TabLink>
        )}

        {/* Marketing link */}
        {mc.marketing_events > 0 && (
          <TabLink href={`/customers/${c.id}/marketing_events`}>
            Marketing ({mc.marketing_events})
          </TabLink>
        )}

        {/* Schedules link */}
        {mc.schedules > 0 && (
          <TabLink href={`/customers/${c.id}/schedules`}>
            Schedules ({mc.schedules})
          </TabLink>
        )}

        {/* Reports dropdown - always visible */}
        <TabDropdown label="Reports" items={reportsItems} />
      </div>

      {/* Add dropdown */}
      {addItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1.5 text-sm outline-none">
            <CirclePlus className="h-4 w-4" />
            <span>Add</span>
            <ChevronDown className="h-3 w-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {addItems.map((item) => (
              <DropdownMenuItem key={item.href} asChild>
                <a href={item.href} className="cursor-pointer">
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  return (
    <AppLayout topBar={customerTabsBar}>
      <Head title={c.display_name} />

      <div className="p-6">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-hpi-dark-gray text-xl font-semibold uppercase">
              {c.display_name}
            </h1>
            <Link
              href={`/customers/${c.id}/edit`}
              className="text-hpi-med-blue hover:text-hpi-active-blue"
            >
              <SquarePen className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Customer info - two columns */}
        <div className="mb-6 grid grid-cols-[1fr_340px] gap-6">
          {/* Left: contact + address */}
          <div className="text-hpi-dark-gray space-y-1 text-sm">
            {c.company_name && (
              <div>
                <span className="font-semibold">Company:</span> {c.company_name}
              </div>
            )}
            {[c.first_name, c.last_name].some(Boolean) && (
              <div>
                <span className="font-semibold">Contact:</span>{" "}
                {[c.first_name, c.last_name].filter(Boolean).join(" ")}
              </div>
            )}

            {[c.address, c.city, c.state].some(Boolean) && (
              <div className="mt-2">
                <span className="font-semibold">Address:</span>
                <div className="ml-4">
                  {c.address && <div>{c.address}</div>}
                  {c.address2 && <div>{c.address2}</div>}
                  {[c.city, c.state, c.zip].some(Boolean) && (
                    <div>
                      {c.city}
                      {c.city && c.state && ", "}
                      {c.state} {c.zip}
                    </div>
                  )}
                </div>
              </div>
            )}

            {c.client_type && (
              <div className="mt-2">
                <span className="font-semibold">Type:</span> {c.client_type}
              </div>
            )}
            {c.sales_rep && (
              <div>
                <span className="font-semibold">Sales Rep:</span> {c.sales_rep}
              </div>
            )}

            {marketing_categories.length > 0 && (
              <div className="mt-2">
                <span className="font-semibold">Categories:</span>{" "}
                {marketing_categories.join(", ")}
              </div>
            )}
          </div>

          {/* Right: phones, email, website */}
          <div className="text-hpi-dark-gray space-y-1 text-sm">
            {c.office_phone && (
              <div>
                <span className="font-semibold">Office:</span>{" "}
                {formatPhone(c.office_phone)}
              </div>
            )}
            {c.cell_phone && (
              <div>
                <span className="font-semibold">Cell:</span>{" "}
                {formatPhone(c.cell_phone)}
              </div>
            )}
            {c.home_phone && (
              <div>
                <span className="font-semibold">Home:</span>{" "}
                {formatPhone(c.home_phone)}
              </div>
            )}
            {c.fax && (
              <div>
                <span className="font-semibold">Fax:</span> {formatPhone(c.fax)}
              </div>
            )}
            {c.email && (
              <div>
                <span className="font-semibold">Email:</span>{" "}
                <a
                  href={`mailto:${c.email}`}
                  className="text-hpi-active-blue hover:underline"
                >
                  {c.email}
                </a>
              </div>
            )}
            {c.website && (
              <div>
                <span className="font-semibold">Website:</span>{" "}
                <a
                  href={ensureUrl(c.website)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hpi-active-blue hover:underline"
                >
                  {c.website}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Open Cases */}
        {open_cases.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<OpenCase>
              title="Open Cases"
              columns={openCaseColumns}
              data={open_cases}
              keyFn={(row) => row.id}
              rowHref={(row) => `/cases/${row.id}`}
            />
          </div>
        )}

        {/* Notes */}
        {c.notes && (
          <div className="text-hpi-dark-gray mb-4 text-sm whitespace-pre-wrap">
            {c.notes}
          </div>
        )}

        {/* Internal Notes */}
        {c.internal_notes && (
          <div className="text-hpi-red mb-4 text-sm whitespace-pre-wrap">
            <span className="font-semibold">Internal Notes:</span>{" "}
            {c.internal_notes}
          </div>
        )}

        {/* Referral info */}
        {referral_customer && (
          <div className="text-hpi-dark-gray mb-4 text-sm">
            <span className="font-semibold">Referred by:</span>{" "}
            <Link
              href={`/customers/${referral_customer.id}`}
              className="text-hpi-active-blue hover:underline"
            >
              {referral_customer.name}
            </Link>
          </div>
        )}
        {vendor_referral && (
          <div className="text-hpi-dark-gray mb-4 text-sm">
            <span className="font-semibold">Referred by:</span>{" "}
            <Link
              href={`/vendors/${vendor_referral.id}`}
              className="text-hpi-active-blue hover:underline"
            >
              {vendor_referral.name}
            </Link>
          </div>
        )}

        {/* Case/Referral History */}
        {hasHistory && (
          <div className="mb-6">
            <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
              <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
                Case/Referral History
              </h3>
            </div>
            <div className="border-hpi-light-gray overflow-x-auto border border-t-0 bg-white">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-hpi-light-gray border-b">
                    <th className="text-hpi-active-blue px-3 py-2 text-left font-medium">
                      Year
                    </th>
                    {MONTH_ABBR.map((m) => (
                      <th
                        key={m}
                        className="text-hpi-active-blue px-2 py-2 text-center font-medium"
                      >
                        {m}
                      </th>
                    ))}
                    <th className="text-hpi-active-blue px-3 py-2 text-center font-medium">
                      Total
                    </th>
                    <th className="text-hpi-active-blue px-3 py-2 text-right font-medium">
                      Est Billable
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {case_history.map((yearRow) => {
                    const yearTotal = yearRow.months.reduce(
                      (s, m) => s + m.qty,
                      0,
                    )
                    const yearValue = yearRow.months.reduce(
                      (s, m) => s + m.value,
                      0,
                    )
                    if (yearTotal === 0) return null
                    return (
                      <tr
                        key={yearRow.year}
                        className="border-hpi-light-gray/50 hover:bg-hpi-pale-gray border-b"
                      >
                        <td className="text-hpi-dark-gray px-3 py-1.5 font-semibold">
                          {yearRow.year}
                        </td>
                        {yearRow.months.map((m) => (
                          <td
                            key={m.month}
                            className="text-hpi-dark-gray px-2 py-1.5 text-center"
                          >
                            {m.qty > 0 ? m.qty : ""}
                          </td>
                        ))}
                        <td className="text-hpi-dark-gray px-3 py-1.5 text-center font-semibold">
                          {yearTotal}
                        </td>
                        <td className="text-hpi-dark-gray px-3 py-1.5 text-right">
                          {formatCurrency(yearValue)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-hpi-light-gray bg-hpi-pale-gray border-t">
                    <td
                      colSpan={13}
                      className="text-hpi-dark-gray px-3 py-2 text-right font-semibold"
                    >
                      Total
                    </td>
                    <td className="text-hpi-dark-gray px-3 py-2 text-center font-semibold">
                      {totalCases}
                    </td>
                    <td className="text-hpi-dark-gray px-3 py-2 text-right font-semibold">
                      {formatCurrency(totalValue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Audit trail */}
        <div className="text-hpi-med-gray text-xs">
          {c.created_at && (
            <span>
              Added on {formatDateTime(c.created_at)}
              {c.created_by_name && <span> by {c.created_by_name}</span>}
            </span>
          )}
          {c.updated_at && (
            <span>
              , Last updated {formatDateTime(c.updated_at)}
              {c.updated_by_name && <span> by {c.updated_by_name}</span>}
            </span>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
